import React from 'react';
import type { CSSProperties } from 'react';
import { backgroundFillColor, fillContrastTextColor } from './blockBackgroundStyle';
import type { BlockBackground } from './blockBackgroundStyle';

/**
 * WEB-1440 — ПОВЕРХНОСТЬ САМОГО ЭЛЕМЕНТА, а не подложка вокруг него.
 *
 * «Фон блока» (WEB-955/1062, withBlockBackground) красит обёртку ВОКРУГ блока.
 * Внутренняя карточка — стикер тизера, панель сравнения «сейчас/цель», билет
 * промо, шкала ИМТ — рисуется самим блок-компонентом и берёт цвет из
 * переменных темы: заливку из `--color-bg-surface`, текст из
 * `--color-text-on-surface` (инвариант WEB-966). Настроить её автору было
 * нечем: на тёмной палитре светлый текст ложился на светлую карточку —
 * «белое на белом» (репро WEB-1440).
 *
 * Точечный путь (WEB-1430 сделал это для micro_feedback отдельными полями
 * feedbackInnerBackground/feedbackTextColor/...) на ~30 типов блоков означал бы
 * ~30 копипаст поля + панели + рендера. Вместо этого — ОДНА обёртка в общей
 * точке рендера (рядом с withBlockFont/withBlockBackground), которая
 * переопределяет для поддерева блока сами переменные поверхности. Их уже
 * читают ~35 мест PublicBlockRenderer(Extended) и все блок-компоненты волны
 * WEB-926, поэтому новых веток по типам не появляется вообще.
 *
 * Ограничение осознанное: через переменную выражается только ЦВЕТ заливки
 * (сплошной или полупрозрачный). Блюр/стекло требуют backdrop-filter на самом
 * элементе карточки — это проброс стиля в каждый блок-компонент, вынесено в
 * хвост карточки.
 */

export interface BlockInnerSurfaceFields {
  /** Заливка самого элемента: цвет + плотность. Absent/'none' = как в теме. */
  innerBackground?: BlockBackground;
  /** Цвет текста ВНУТРИ элемента. Absent/пусто = цвет темы (поведение прежнее). */
  innerTextColor?: string;
  /** Базовый кегль содержимого элемента, px. Absent = как раньше. */
  innerFontSize?: number;
}

/**
 * WEB-1702 п.2 — СТАРШИНСТВО ЦВЕТА ТЕКСТА. Решение владельца 2026-08-25.
 *
 *   1. ручная покраска куска текста (панелька форматирования) — сильнее всего;
 *   2. «Цвет текста внутри элемента» ЭТОГО блока (innerTextColor);
 *   3. общий цвет текста воронки (settings.textColor, его же ставит ИИ-чат);
 *   4. цвет темы/палитры.
 *
 * Пункты 2 и 3 стояли наоборот, и не по замыслу, а по механике: общий цвет
 * воронки рендер запекал ИНЛАЙНОМ прямо на элементе (`textColor ?? var(...)`),
 * а блочный действует через переменные обёртки — inline на самом элементе
 * сильнее унаследованного. Пока общего цвета у воронки не было, поле блока
 * работало; стоило ИИ-чату задать общий — поле молча умирало у всех текстовых
 * и вопросных блоков. Автор не связывал причину со следствием: «сломалось» не
 * в момент правки поля.
 *
 * Правило применяется в ОДНОЙ точке на рендерер: везде, где общий цвет воронки
 * перебивал переменную `--color-text-*`, спрашивается сначала блочный цвет.
 * Туда, где `textColor` идёт НЕ как цвет текста блока (надпись кнопки через
 * primaryButtonStyle), это правило не нужно: у кнопок тот же порядок держит
 * `buttonSurfaceStyle` — он кладёт `innerTextColor` автора ПОСЛЕ цвета из
 * темы и авто-контраста. WEB-1696: прежняя приписка «у кнопок поля нет вовсе»
 * была верна лишь про панель и лишь до 01.09 — поле действовало всегда, а
 * контрол пропал по однобокому признаку живости; он вернулся, кнопки снова в
 * INNER_TEXT_COLOR_BLOCK_TYPES.
 *
 * ⚠ Разворот МЕНЯЕТ вид уже опубликованных воронок там, где заданы ОБА цвета —
 * владелец пошёл на это сознательно. Там, где блочный цвет не задан, вид не
 * меняется ни на пиксель (гард в тестах).
 */
export function resolveContentTextColor(
  fields: BlockInnerSurfaceFields,
  funnelTextColor: string | undefined,
): string | undefined {
  return fields.innerTextColor?.trim() || funnelTextColor;
}

/**
 * WEB-1711 — типы, чей рендер САМ запекает значение «Заливки элемента» в свою
 * разметку, и которым публиковать его ещё и переменной НЕЛЬЗЯ.
 *
 * Обёртка ниже подменяет `--color-bg-surface` всему поддереву блока. Это
 * верно, пока «элемент» блока — одна поверхность. У «Баннера срочности» их
 * две: сам баннер и кнопка НА нём. Баннер переменную не читал (фон был зашит
 * терракотовым), а кнопка читала — и настройка заливки красила кнопку вместо
 * баннера (репорт владельца). Теперь баннер берёт значение поля напрямую, а
 * переменная для его поддерева не публикуется вовсе: мост разорван в
 * источнике, а не залатан на стороне кнопки. Кнопка продолжает читать
 * `--color-bg-surface` ТЕМЫ — вид воронки без настроек не меняется.
 *
 * Цвет текста и кегль публикуются как раньше: их читателем остаётся текст
 * баннера, и настройка автора обязана до него доезжать.
 */
export const SELF_PAINTED_INNER_FILL_TYPES: ReadonlySet<string> = new Set([
  'urgency_banner',
  /**
   * WEB-1677, ревизия пачки 8 (02.09) — ТА ЖЕ ФОРМА, ВТОРОЙ СЛУЧАЙ. Шкала ИМТ
   * получила покраску корня значением (b583b151), но осталась вне реестра: у
   * блока внутри вторая поверхность — кружок-ползунок 24×24, единственный
   * читатель `--color-bg-surface` в этой разметке. Обёртка публиковала ему тот
   * же цвет, что лёг в подложку, и маркер сливался с фоном. Мост рвётся в
   * источнике, как у баннера; ползунок остаётся на переменной ТЕМЫ, поэтому
   * воронка без настройки не меняется ни на пиксель.
   */
  'bmi_scale',
]);

/**
 * Поля поверхности, которые отдаются ОБЁРТКЕ для этого блока. Для типов из
 * списка выше заливка исключается — её уже применил сам рендер.
 */
export function innerSurfaceFieldsOf(
  block: BlockInnerSurfaceFields & { type: string },
): BlockInnerSurfaceFields {
  if (!SELF_PAINTED_INNER_FILL_TYPES.has(block.type)) return block;
  return {
    innerTextColor: block.innerTextColor,
    innerFontSize: block.innerFontSize,
  };
}

/**
 * CSS-переменные поверхности. undefined = настраивать нечего (узел не надо
 * оборачивать вовсе — старая воронка не получит ни лишнего div, ни пикселя
 * разницы, инвариант WEB-1062 п.5).
 */
export function blockInnerSurfaceStyle(
  fields: BlockInnerSurfaceFields,
): CSSProperties | undefined {
  // WEB-3684: поле читает одна дверь (toBlockBackground внутри
  // backgroundFillColor) — строка, 'none' и чужая форма дают undefined, своей
  // проверки `.style !== 'none'` здесь нет.
  const fill = backgroundFillColor(fields.innerBackground);
  const textColor = fields.innerTextColor?.trim() || undefined;
  const fontSize = typeof fields.innerFontSize === 'number' ? fields.innerFontSize : undefined;

  if (!fill && !textColor && fontSize === undefined) return undefined;

  const style: Record<string, string | number> = {};

  // Кегль обязан быть «живым» размером шрифта, а не только переменной: кегли
  // внутри блоков волны WEB-926 переведены на em и считаются ОТ ЭТОЙ обёртки.
  // Поэтому с кеглем обёртка — обычный блок, без него — display:contents,
  // чтобы не создавать бокс и не трогать раскладку (приём withBlockFont).
  if (fontSize !== undefined) {
    style.fontSize = fontSize;
  } else {
    style.display = 'contents';
  }

  if (fill) {
    style['--color-bg-surface'] = fill;
  }

  // Цвет текста: свой сильнее авто-контраста. Без своего цвета, но со своей
  // заливкой доклеивается контраст-эвристика по цвету заливки — та же формула
  // и ТОТ ЖЕ ГЕЙТ НЕПРОЗРАЧНОСТИ, что у кнопки и значка гарантии
  // (fillContrastTextColor, WEB-1859). Иначе тёмная заливка элемента получила
  // бы тёмный текст темы — тот же класс дефекта, ради которого правку и
  // делали; а по плёнке (низкая альфа или малая плотность пресета) цвет не
  // считается вовсе — переменная не ставится, решает тема.
  const resolvedText = textColor ?? fillContrastTextColor(fields.innerBackground);

  if (resolvedText) {
    style['--color-text-on-surface'] = resolvedText;
  }
  if (textColor) {
    // Свой цвет автора — это «весь текст этого блока», а не только тот, что
    // помечен «на поверхности»: блоки красят подписи и через
    // --color-text-primary/secondary/tertiary, и просто наследованием.
    style['--color-text-primary'] = textColor;
    style['--color-text-secondary'] = textColor;
    style['--color-text-tertiary'] = textColor;
    style.color = textColor;
  }

  return style as CSSProperties;
}

/**
 * Обернуть готовый узел блока в область переменных поверхности. Overload-пара
 * зеркалит withBlockFont/withBlockBackground: узкий вариант для
 * PublicBlockRendererExtended (ReactElement | null), широкий — для
 * PublicBlockRenderer (делегирующий случай может вернуть undefined).
 */
export function withBlockInnerSurface(
  fields: BlockInnerSurfaceFields,
  node: React.ReactElement | null,
): React.ReactElement | null;
export function withBlockInnerSurface(
  fields: BlockInnerSurfaceFields,
  node: React.ReactNode,
): React.ReactNode;
export function withBlockInnerSurface(
  fields: BlockInnerSurfaceFields,
  node: React.ReactNode,
): React.ReactNode {
  if (node === null || node === undefined || node === false) return node;
  const style = blockInnerSurfaceStyle(fields);
  if (!style) return node;
  return (
    <div data-block-inner-surface="1" style={style}>
      {node}
    </div>
  );
}
